import {
  GenericError,
  SystemError,
  UserError,
  isK2Error,
  isSystemError,
  isUserError,
} from 'shared/utils/errors'
import logger, { ErrorOptions } from '../logger'
import { isUserFacingGrpcStatusCode } from 'shared/utils/errors/rpc.utils'
import {
  SerializableServerActionError,
  ServerActionResponse,
} from './server-action.schema'

export type ServerActionUseCaseOptions<T> = {
  loggerOptions: ErrorOptions | ((request: T) => ErrorOptions)
  defaultErrorMessage: string
  /**
   * Show a SystemError's wire message even when its gRPC status is one the
   * transport hides by default. Only for backends that write user-facing text.
   */
  exposeServerErrorMessage?: boolean
  config?: {
    accessToken?: string
  }
}

export type ProcessServerActionErrorOptions = Pick<
  ServerActionUseCaseOptions<never>,
  'exposeServerErrorMessage'
>

type NewInnerCallback<RequestInput extends object, ResponseType> = (
  request: RequestInput,
  config?: ServerActionUseCaseOptions<RequestInput>['config'],
) => Promise<ResponseType>

type NewOuterCallback<RequestInput extends object, ResponseType> = (
  request: RequestInput,
  options?: ServerActionUseCaseOptions<RequestInput>['config'],
) => Promise<ServerActionResponse<ResponseType>>

/**
 * This method wraps a grpc-call to k2 and processes the response and error
 * - Errors caught are logged and returned as K2GenericErrors, with user-friendly messages
 * - Responses are processed to be used as GuaranteedResponses
 * - this method always returns an object with a response and error property so Next doesn't intercept the errors thrown and obfuscate their message.
 * TODO: migrate the name to `K2UseCase` and delete the old one, once we've migrated all of the old methods
 */
export function ServerActionUseCase<RequestInput extends object, ResponseType>(
  callback: NewInnerCallback<RequestInput, ResponseType>,
  {
    loggerOptions,
    defaultErrorMessage,
    exposeServerErrorMessage,
  }: ServerActionUseCaseOptions<RequestInput>,
): NewOuterCallback<RequestInput, ResponseType> {
  return async (
    request: RequestInput,
    config?: ServerActionUseCaseOptions<RequestInput>['config'],
  ): Promise<ServerActionResponse<ResponseType>> => {
    try {
      const response = await callback(request, config)
      return _successResponse(response)
    } catch (error) {
      // Log the error immediately as it's received, so the logger can decide if it should be logged.
      logger.error(
        error,
        typeof loggerOptions === 'function'
          ? loggerOptions(request)
          : loggerOptions,
      )

      // sanitize the error so it can be returned to the client with a user-friendly message
      const processedError = _processServerActionError(
        error,
        defaultErrorMessage,
        { exposeServerErrorMessage },
      )

      return _errorResponse(processedError)
    }
  }
}

/**
 * # Processing
 * - This method ensures that a user-friendly error message is returned in a UserError or a handled K2GenericError
 * - If a K2GenericError is unhandled, the default error message is used
 * - If the error is already a UserError, it is returned as is
 * - If the error is an instance of Error, it is wrapped in a UserError
 * - If the error is anything else, a new UserError is created with the default message
 * - A SystemError keeps its wire message when opted in or status is user-facing
 *
 * # Logging
 * - It also sets a return error's 'handled' property to true, which will be ignored by the logger if logged as an error
 * - For this reason, we need to log errors from K2's grpc-interface before returning them from this processor
 *
 * # Usage
 * ```
 * try {
 *  ...
 * } catch (error) {
 *  logger.error(error, {feature: 'my-feature'})
 *  throw processK2Error(error, 'default error message')
 * }
 * ```
 */
export function _processServerActionError(
  error: unknown,
  defaultMessage: string,
  { exposeServerErrorMessage = false }: ProcessServerActionErrorOptions = {},
): UserError | SystemError {
  if (isK2Error(error)) {
    return error.toApplicationError(defaultMessage)
  } else if (isUserError(error)) {
    return error
  } else if (isSystemError(error)) {
    const hasWireMessage =
      Boolean(error.message) && error.message !== 'Unknown Error'
    const exposeUserFacingGrpcMessage =
      hasWireMessage &&
      (isUserFacingGrpcStatusCode(error.metadata?.grpcStatusCode) ||
        exposeServerErrorMessage)
    error.userMessage =
      error.handled || exposeUserFacingGrpcMessage
        ? error.message
        : defaultMessage
    return error
  } else if (error instanceof Error) {
    return new UserError(defaultMessage, { originalError: error })
  } else {
    return new UserError(defaultMessage)
  }
}

/**
 * This method serializes a K2GenericError or UserError into a SerializableServerActionError so that a message can be returned to the client
 */
export function _serializeProcessedError(
  error: GenericError | UserError | SystemError,
): SerializableServerActionError {
  const metadata: SerializableServerActionError['metadata'] = {}
  let message = error.message

  if (isSystemError(error)) {
    message = error.userMessage
  }

  if (error.metadata?.grpcStatusCode)
    metadata.grpcStatusCode = error.metadata?.grpcStatusCode

  return {
    message,
    metadata: Object.keys(metadata).length > 0 ? metadata : null,
  }
}

/**
 * This method serializes a response to ensure that it is a JSON serializable object, because server actions cannot return complex objects
 */
export function _serializeResponse<ResponseType>(
  response: ResponseType,
): ResponseType {
  return JSON.parse(JSON.stringify(response))
}

/**
 * This method returns a success response for a server action
 */
export function _successResponse<T>(response: T): ServerActionResponse<T> {
  return {
    response: _serializeResponse(response),
    error: null,
  }
}

/**
 * This method returns an error response for a server action
 */
export function _errorResponse<T>(
  error: GenericError | UserError,
): ServerActionResponse<T> {
  return {
    response: null,
    error: _serializeProcessedError(error),
  }
}
